import type { MeResponse, Permission } from '@taskall/contracts';
import { useQueryClient } from '@tanstack/react-query';
import {
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import * as React from 'react';
import { ApiError, api } from '@/lib/api';
import { auth } from '@/lib/firebase';

const SCHOOL_KEY = 'taskall.schoolId';

type Status = 'loading' | 'authenticated' | 'anonymous';

export interface AuthContextValue {
  status: Status;
  session: MeResponse | null;
  schoolId: string | null;
  can: (permission: Permission) => boolean;
  login: (input: { schoolCode?: string; email: string; password: string }) => Promise<MeResponse>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  /**
   * Troca a própria senha (API → Firebase Authentication) e renova a sessão com a senha nova.
   * `currentPassword` é exigida fora do primeiro acesso (reautenticação).
   */
  changePassword: (input: { newPassword: string; confirmPassword: string; currentPassword?: string }) => Promise<MeResponse>;
  refresh: () => Promise<void>;
  /** Troca a escola da sessão (equipe TaskAll ou quem tem acesso a mais de uma escola). */
  switchSchool: (schoolId: string) => Promise<MeResponse>;
  setSession: (session: MeResponse) => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

function readSchool(): string | null {
  try {
    return window.localStorage.getItem(SCHOOL_KEY);
  } catch {
    return null;
  }
}
function writeSchool(id: string | null): void {
  try {
    if (id) window.localStorage.setItem(SCHOOL_KEY, id);
    else window.localStorage.removeItem(SCHOOL_KEY);
  } catch {
    // armazenamento indisponível: segue apenas em memória
  }
}

/** Mensagens amigáveis para erros do Firebase Auth (sem revelar se o e-mail existe). */
export function authErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'SCHOOL_ACCESS_DENIED') return 'Código da escola inválido ou sem acesso para este usuário.';
    if (error.code === 'PLATFORM_EMAIL_NOT_VERIFIED') return 'Conta da equipe TaskAll sem e-mail verificado. Peça a um administrador para prepará-la (script user:create).';
    return error.message;
  }
  const code = (error as { code?: string })?.code ?? '';
  if (['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found', 'auth/invalid-email'].includes(code)) {
    return 'E-mail ou senha incorretos.';
  }
  if (code === 'auth/too-many-requests') return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
  if (code === 'auth/user-disabled') return 'Usuário desativado. Procure a secretaria.';
  if (code === 'auth/network-request-failed') return 'Sem conexão. Verifique sua internet.';
  return 'Não foi possível entrar. Tente novamente.';
}

/**
 * Sessão: Firebase Auth (ID token) + GET /me validando a membership na escola.
 * O schoolId selecionado nunca é confiado pelo backend sem verificação.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = React.useState<Status>('loading');
  const [session, setSessionState] = React.useState<MeResponse | null>(null);
  const loggingIn = React.useRef(false);

  const setSession = React.useCallback((s: MeResponse) => {
    setSessionState(s);
    writeSchool(s.school?.id ?? null);
    setStatus('authenticated');
  }, []);

  const loadSession = React.useCallback(async () => {
    const me = await api<MeResponse>('/me', { query: { schoolId: readSchool() ?? undefined } });
    setSession(me);
  }, [setSession]);

  // A API valida o acesso à escola (membership ou equipe TaskAll) antes de trocar.
  const switchSchool = React.useCallback(
    async (schoolId: string) => {
      const me = await api<MeResponse>('/me', { query: { schoolId } });
      setSession(me);
      return me;
    },
    [setSession],
  );

  React.useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      if (loggingIn.current) return;
      if (!user) {
        setSessionState(null);
        setStatus('anonymous');
        return;
      }
      try {
        await loadSession();
      } catch (error) {
        // Só encerra a sessão do Firebase quando a API recusa o usuário (401/403). Falhas de rede ou
        // requisições abortadas (ex.: navegação durante o carregamento) não devem apagar o login persistido.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          await signOut(auth);
          writeSchool(null);
        }
        setSessionState(null);
        setStatus('anonymous');
      }
    });
  }, [loadSession]);

  const login = React.useCallback<AuthContextValue['login']>(
    async ({ schoolCode, email, password }) => {
      loggingIn.current = true;
      try {
        const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
        const token = await cred.user.getIdToken();
        try {
          const me = await api<MeResponse>('/me', { query: { schoolCode: schoolCode?.trim() || undefined }, token });
          queryClient.clear();
          setSession(me);
          return me;
        } catch (error) {
          await signOut(auth);
          throw error;
        }
      } finally {
        loggingIn.current = false;
      }
    },
    [queryClient, setSession],
  );

  const logout = React.useCallback(async () => {
    await signOut(auth);
    writeSchool(null);
    queryClient.clear();
    setSessionState(null);
    setStatus('anonymous');
  }, [queryClient]);

  const resetPassword = React.useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email.trim());
  }, []);

  const changePassword = React.useCallback<AuthContextValue['changePassword']>(
    async ({ newPassword, confirmPassword, currentPassword }) => {
      const user = auth.currentUser;
      if (!user?.email) throw new Error('Sessão expirada. Faça login novamente.');
      if (currentPassword) {
        await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, currentPassword));
      }
      await api('/me/password', { method: 'POST', body: { newPassword, confirmPassword } });
      // A troca encerra a sessão anterior: entra de novo com a senha nova (token sem a pendência).
      loggingIn.current = true;
      try {
        await signInWithEmailAndPassword(auth, user.email, newPassword);
        const me = await api<MeResponse>('/me', { query: { schoolId: readSchool() ?? undefined } });
        setSession(me);
        return me;
      } finally {
        loggingIn.current = false;
      }
    },
    [setSession],
  );

  const value = React.useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      schoolId: session?.school?.id ?? null,
      can: (p) => !!session?.permissions.includes(p),
      login,
      logout,
      resetPassword,
      changePassword,
      refresh: loadSession,
      switchSchool,
      setSession,
    }),
    [status, session, login, logout, resetPassword, changePassword, loadSession, switchSchool, setSession],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}

/** schoolId da sessão autenticada (rotas protegidas). */
export function useSchoolId(): string {
  const { schoolId } = useAuth();
  if (!schoolId) throw new Error('Sessão sem escola selecionada');
  return schoolId;
}
